import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ModuleEntitlementAdmin } from '@/api/endpoints/admin';

export interface ModuleDraft {
  key: string;
  habilitado: boolean;
  cota: number | null;
}

/**
 * Editor de módulos/cotas de um tenant. `allowedKeys` (revenda) limita quais
 * módulos podem ser habilitados — os demais aparecem bloqueados.
 */
export function ModuleEditor({
  modules,
  allowedKeys,
  saving,
  onSave,
}: {
  modules: ModuleEntitlementAdmin[];
  allowedKeys?: Set<string> | null;
  saving: boolean;
  onSave: (draft: ModuleDraft[]) => void;
}) {
  const [state, setState] = useState<Record<string, ModuleDraft>>(() =>
    Object.fromEntries(modules.map((m) => [m.key, { key: m.key, habilitado: m.habilitado, cota: m.cota }])),
  );

  const grupos = useMemo(() => {
    const g = new Map<string, ModuleEntitlementAdmin[]>();
    for (const m of modules) {
      const arr = g.get(m.categoria) ?? [];
      arr.push(m);
      g.set(m.categoria, arr);
    }
    return Array.from(g.entries());
  }, [modules]);

  const toggle = (key: string) =>
    setState((s) => ({ ...s, [key]: { ...s[key], habilitado: !s[key].habilitado } }));
  const setCota = (key: string, v: string) =>
    setState((s) => ({ ...s, [key]: { ...s[key], cota: v.trim() === '' ? null : Math.max(0, Number(v) || 0) } }));

  const blocked = (key: string) => !!allowedKeys && !allowedKeys.has(key);

  return (
    <div className="space-y-4">
      {grupos.map(([cat, mods]) => (
        <div key={cat} className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{cat}</p>
          {mods.map((m) => {
            const d = state[m.key];
            const off = blocked(m.key);
            return (
              <div
                key={m.key}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2',
                  off && 'opacity-50',
                )}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{m.nome}</p>
                  <p className="text-[11px] text-muted-foreground">{m.key}{off && ' · indisponível no seu plano'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    value={d.cota ?? ''}
                    onChange={(e) => setCota(m.key, e.target.value)}
                    placeholder="∞"
                    disabled={off || !d.habilitado}
                    className="h-8 w-20 text-right"
                    title="Cota (vazio = ilimitado)"
                  />
                  <button
                    type="button"
                    onClick={() => !off && toggle(m.key)}
                    disabled={off}
                    role="switch"
                    aria-checked={d.habilitado}
                    className={cn(
                      'relative h-6 w-11 shrink-0 rounded-full transition-colors',
                      d.habilitado ? 'bg-status-running' : 'bg-secondary',
                      off && 'cursor-not-allowed',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all',
                        d.habilitado ? 'left-[22px]' : 'left-0.5',
                      )}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div className="flex justify-end">
        <Button onClick={() => onSave(Object.values(state))} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar módulos'}
        </Button>
      </div>
    </div>
  );
}
